import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronUp, LogOut, User as UserIcon } from 'lucide-react'
import sidebarLogo from '@/assets/sidebar/logo.svg'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { logout } from '@/store/authSlice'
import { generalNav, visibleNav } from '@/config/nav'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/toast'
import type { User } from '@/types'

const SEGMENT_NAV_KEY: Record<string, string> = {
  dashboard: 'dashboard',
  calendar: 'calendar',
  jobs: 'calendar',
  clients: 'clients',
  quotes: 'quotes',
  invoices: 'invoices',
  'credit-notes': 'creditNotes',
  payments: 'payments',
  contracts: 'contracts',
  tasks: 'tasks',
  technicians: 'technicians',
  notifications: 'notifications',
  settings: 'settings',
}

function navKeyForPath(to: string): string | undefined {
  return SEGMENT_NAV_KEY[to.replace(/^\//, '').split(/[/?]/)[0]]
}

function SidebarBrand() {
  const { t } = useTranslation('shell')

  return (
    <div className='flex h-[72px] w-full items-center px-[10px] py-4'>
      <img
        src={sidebarLogo}
        alt={t('brand.logoAlt')}
        className='h-[39px] w-[176px] shrink-0'
      />
    </div>
  )
}

function SidebarNav() {
  const { t } = useTranslation('shell')
  const { pathname } = useLocation()
  const user = useAppSelector(s => s.auth.user)
  const items = visibleNav(generalNav, user?.permissions)

  return (
    <div className='flex min-h-0 flex-1 flex-col px-[10px] py-4'>
      <nav className='flex flex-col gap-2' aria-label={t('nav.ariaLabel')}>
        {items.map(item => {
          const Icon = item.icon
          const navKey = navKeyForPath(item.to)
          const sectionActive =
            item.childPaths?.some(p => pathname.startsWith(p)) ?? false
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex h-10 items-center gap-1.5 overflow-hidden rounded-xl px-3 py-2.5 text-sm font-medium leading-[1.4] text-[#aeb4c0] transition-colors',
                  isActive || sectionActive
                    ? 'bg-[#ff5a1f] text-white shadow-[0_1px_8px_-1px_rgba(255,90,31,0.2),0_1px_4px_-1px_#ff5a1f]'
                    : 'hover:bg-[#1b1e24] hover:text-white'
                )
              }
            >
              <Icon className='h-5 w-5 shrink-0' />
              <span className='min-w-0 flex-1 truncate'>
                {navKey ? t(`nav.${navKey}`) : item.label}
              </span>
            </NavLink>
          )
        })}
      </nav>
    </div>
  )
}

function SidebarAccount({ user }: { user: User }) {
  const { t } = useTranslation(['shell', 'auth'])
  const [menuOpen, setMenuOpen] = useState(false)
  const [logoutOpen, setLogoutOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const roleLabel = t(`roles.${user.role}`, {
    defaultValue: user.role,
  })
  const accountInitial =
    (user.full_name ?? user.email ?? '?').trim().charAt(0).toUpperCase() || '?'

  const handleLogout = async () => {
    setBusy(true)
    try {
      await dispatch(logout(undefined)).unwrap()
    } catch {
      // Forced client-side clear already handled in the thunk.
    } finally {
      setBusy(false)
      setLogoutOpen(false)
      toast({ title: t('auth:logout.success'), variant: 'success' })
      navigate('/login', { replace: true })
    }
  }

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            className='flex w-full items-center gap-2 overflow-hidden rounded-xl bg-[#1e2028] px-3 py-2 text-left outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0'
            aria-label={t('account.menuLabel')}
          >
            <div className='flex min-w-0 flex-1 items-start gap-3'>
              <Avatar
                name={user.full_name}
                src={user.avatar}
                className='h-10 w-10 bg-[#ff5a1f] font-heading text-base font-semibold leading-[1.2] text-white'
              >
                {user.avatar ? undefined : accountInitial}
              </Avatar>
              <div className='flex min-w-0 flex-1 flex-col gap-[2px]'>
                <p className='truncate text-sm font-medium leading-[1.4] text-white'>
                  {user.full_name ?? user.email}
                </p>
                <p className='truncate text-sm font-normal leading-[1.4] text-[#6c7285]'>
                  {roleLabel}
                </p>
              </div>
            </div>
            <ChevronUp
              className={cn(
                'h-5 w-5 shrink-0 text-[#6c7285] transition-transform duration-200 ease-out motion-reduce:transition-none',
                menuOpen && 'rotate-180'
              )}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side='top'
          align='start'
          sideOffset={0}
          className='w-[218px] rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0px_1px_4px_-1px_rgba(38,45,66,0.05),0px_1px_8px_-1px_rgba(38,45,66,0.1)]'
        >
          <DropdownMenuItem
            className='h-12 cursor-pointer gap-2 rounded-lg p-2 text-sm font-medium leading-[1.4] text-[#171a20] focus:bg-[#f5f6fa] focus:text-[#171a20] data-[highlighted]:bg-[#f5f6fa] data-[highlighted]:text-[#171a20]'
            onSelect={() => {
              setMenuOpen(false)
              navigate('/privacy-data')
            }}
          >
            <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f5f6fa] text-[#272c36]'>
              <UserIcon className='size-4' />
            </span>
            {t('account.myAccount')}
          </DropdownMenuItem>
          <DropdownMenuItem
            className='h-12 cursor-pointer gap-2 rounded-lg p-2 text-sm font-medium leading-[1.4] text-[#d63b33] focus:bg-[#f5f6fa] focus:text-[#d63b33] data-[highlighted]:bg-[#f5f6fa] data-[highlighted]:text-[#d63b33]'
            onSelect={() => {
              setMenuOpen(false)
              setLogoutOpen(true)
            }}
          >
            <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#fde9e8] text-[#d63b33]'>
              <LogOut className='size-4' />
            </span>
            {t('auth:logout.label')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <DialogContent className='max-w-sm' showCloseButton={false}>
          <DialogHeader>
            <DialogTitle className='text-base font-semibold'>
              {t('auth:logout.confirmTitle')}
            </DialogTitle>
          </DialogHeader>
          <DialogFooter className='gap-2 sm:gap-2'>
            <Button
              variant='outline'
              onClick={() => setLogoutOpen(false)}
              disabled={busy}
            >
              {t('auth:logout.cancel')}
            </Button>
            <Button onClick={handleLogout} disabled={busy}>
              {busy ? t('auth:logout.loggingOut') : t('auth:logout.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export function Sidebar() {
  const user = useAppSelector(s => s.auth.user)

  return (
    <aside className='sticky top-0 flex h-screen w-[238px] shrink-0 flex-col bg-[#101216] text-sidebar-foreground'>
      <SidebarBrand />
      <div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
        <SidebarNav />
      </div>
      {user && (
        <div className='shrink-0 px-[10px] pb-4'>
          <SidebarAccount user={user} />
        </div>
      )}
    </aside>
  )
}
